/**
 * Billing API Module
 *
 * Instructor Accounting API calls against /api/InstructorAccounting.
 * The backend derives instructorId from the JWT token automatically.
 */

import { apiClient } from '@/api/client'
import type {
  InstructorAccountSummaryDto,
  InstructorAccountDto,
  InstructorAccountingQueryParams,
  CreateInstructorAccountRequest,
  CourseSessionAccountingDto,
} from '@/types/api.types'

export const billingApi = {
  /**
   * Get all instructor accounts for the current trainer (instructorId from JWT).
   * Admins may pass instructorId to view another trainer's accounts.
   * GET /api/InstructorAccounting
   */
  async getAll(params?: InstructorAccountingQueryParams): Promise<InstructorAccountSummaryDto[]> {
    const response = await apiClient.get<InstructorAccountSummaryDto[]>('/InstructorAccounting', {
      params,
    })
    return response.data
  },

  /**
   * Get instructor account detail with course and session breakdown.
   * GET /api/InstructorAccounting/{id}
   */
  async getById(id: number): Promise<InstructorAccountDto> {
    const response = await apiClient.get<InstructorAccountDto>(`/InstructorAccounting/${id}`)
    return response.data
  },

  /**
   * Create a new instructor account (Draft).
   * instructorId is optional — API uses current user's memberId if omitted.
   * billingPeriodType is optional — API uses trainer's default if omitted.
   * POST /api/InstructorAccounting
   */
  async create(request: CreateInstructorAccountRequest): Promise<InstructorAccountDto> {
    const response = await apiClient.post<InstructorAccountDto>('/InstructorAccounting', request)
    return response.data
  },

  /**
   * Submit instructor account (Draft → Submitted).
   * POST /api/InstructorAccounting/{id}/submit
   */
  async submit(id: number): Promise<InstructorAccountDto> {
    const response = await apiClient.post<InstructorAccountDto>(
      `/InstructorAccounting/${id}/submit`
    )
    return response.data
  },

  /**
   * Recalculate all session accountings for an instructor account.
   * Picks up new/changed sessions since creation.
   * POST /api/InstructorAccounting/{id}/recalculate
   */
  async recalculate(id: number): Promise<InstructorAccountDto> {
    const response = await apiClient.post<InstructorAccountDto>(
      `/InstructorAccounting/${id}/recalculate`
    )
    return response.data
  },

  /**
   * Get session accountings for one course within an instructor account.
   * GET /api/InstructorAccounting/{id}/courses/{courseId}/sessions
   */
  async getCourseSessions(
    accountId: number,
    courseId: number
  ): Promise<CourseSessionAccountingDto[]> {
    const response = await apiClient.get<CourseSessionAccountingDto[]>(
      `/InstructorAccounting/${accountId}/courses/${courseId}/sessions`
    )
    return response.data
  },

  /**
   * Revert instructor account (Submitted → Draft).
   * POST /api/InstructorAccounting/{id}/revert
   */
  async revert(id: number): Promise<InstructorAccountDto> {
    const response = await apiClient.post<InstructorAccountDto>(
      `/InstructorAccounting/${id}/revert`,
      { instructorAccountId: id, action: 6 }
    )
    return response.data
  },

  /**
   * Delete an instructor account (only Draft status).
   * DELETE /api/InstructorAccounting/{id}
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`/InstructorAccounting/${id}`)
  },
}
